import { defineMessages, type IntlShape } from 'react-intl';
import type { BlockConfigBase, JSONSchema } from '@plone/types';
import { addAlignmentStyle } from '@redturtle/qubicaamf-theme/components/Blocks/extenders/styles';
import {
  addCtaFieldset,
  type CtaBlockExtender,
} from '@redturtle/qubicaamf-theme/components/Blocks/extenders/cta';

import addBlocksBg from '@redturtle/qubicaamf-theme/components/Blocks/extenders/addBlocksBg';
import addBlocksFullWidth from '@redturtle/qubicaamf-theme/components/Blocks/extenders/addBlocksFullWidth';
import addTitleColor from '@redturtle/qubicaamf-theme/components/Blocks/extenders/addTitleColor';
import addCardStyles from '@redturtle/qubicaamf-theme/components/Blocks/extenders/addCardStyles';
import addBlockLines from '@redturtle/qubicaamf-theme/components/Blocks/extenders/addBlockLines';

type SlateNode = {
  children: { text: string }[];
  styleName: string | null;
  type: string;
};

export interface CardWithImagesData extends CtaBlockExtender, JSONSchema {
  '@type': 'cardwithimages';
  images?: Array<{ '@id': string; image: string }>;
  img_column_width?: string;
  title: string;
  text?: SlateNode[];
}

export const CardWithImagesSchema = ({
  data,
  intl,
}: {
  data: CardWithImagesData;
  intl: IntlShape;
}): JSONSchema => {
  const schema = {
    title: intl.formatMessage(messages.title),
    fieldsets: [
      {
        id: 'default',
        title: 'Default',
        fields: ['title', 'image'],
      },
    ],
    properties: {
      image: {
        title: intl.formatMessage(messages.image_title),
        widget: 'object_browser',
        mode: 'image',
        return: 'single',
      },

      title: {
        title: intl.formatMessage(messages.title_title),
      },
      // TODO necessary?
      // text: {
      //   title: intl.formatMessage(messages.text_title),
      //   widget: 'slate',
      // },
    },
    required: ['title'],
  };

  addCtaFieldset({ schema, intl });
  addAlignmentStyle({
    schema,
    intl,
    formData: data,
    alignments: ['left', 'right'],
  });
  addBlocksBg({ schema, formData: data, intl });
  addCardStyles({ schema, intl, formData: data });
  addBlocksFullWidth({ schema, formData: data, intl });
  addTitleColor({ schema, formData: data, intl });
  addBlockLines({ schema, formData: data, intl });
  return schema;
};

const messages = defineMessages({
  title: {
    id: 'cardwithimages_title',
    defaultMessage: 'Card with images',
  },
  images_title: {
    id: 'cardwithimages_images_title',
    defaultMessage: 'Images',
  },
  image_title: {
    id: 'cardwithimages_image_title',
    defaultMessage: 'Image',
  },
  img_column_width_title: {
    id: 'cardwithimages_img_column_width_title',
    defaultMessage: 'Image column width',
  },
  img_column_width_description: {
    id: 'cardwithimages_img_column_width_description',
    defaultMessage: 'Width of the image column',
  },
  title_title: {
    id: 'Title',
    defaultMessage: 'Title',
  },
  text_title: {
    id: 'Text',
    defaultMessage: 'Text',
  },
});

export interface CardWithImagesConfig
  extends Omit<BlockConfigBase, 'blockSchema'> {
  blockSchema: typeof CardWithImagesSchema;
}
